import type {
  ApprovalDecision,
  ApprovalScope,
  ApprovalSource,
  ApprovalView,
  CredentialInfo,
  MaintenanceInfo,
  MessageView,
  Risk,
  RunnerState,
  SseEvent,
  ThreadDetailResponse,
  ToolCallStatus,
  ToolCallView,
  ToolGate,
} from '@omni/shared';

/**
 * SSE → view state (CONTRACTS §5).
 *
 * One reducer for the whole stream, because the stream itself is one per user
 * and carries thread content, approvals, runner state and credential state
 * together (R5). Approvals in particular must land even with no thread open.
 *
 * Three properties this shape exists to guarantee:
 *
 *  1. **Order-independence.** `tool.end` can arrive before `tool.start` (the
 *     gateway mirrors journal events, and a replay after reconnect can
 *     interleave), so every tool event merges into a placeholder rather than
 *     assuming its predecessor ran. A terminal status is never walked back to
 *     `running` by a late `tool.start`.
 *  2. **O(1) merges.** Items live in `byId` with a separate `order` array; a
 *     10-minute turn with hundreds of tool calls never becomes O(n²).
 *  3. **Hydration never eats the live tail, and never doubles a settled one.**
 *     REST detail (CONTRACTS §4.3) only knows what Tier A has mirrored, which
 *     mid-turn excludes streaming text, so `hydrate` overlays rather than
 *     replaces — keeping live items the payload does not mention. The one
 *     overlay entry ids cannot police is streaming text: a live block is
 *     `txt:<turn>:<block>` and its persisted twin is `msg:<id>`, so once the
 *     payload carries the assistant message of a finished turn, that turn's
 *     live blocks are dropped by turn id instead.
 */

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'offline';

export interface TextItem {
  readonly kind: 'text';
  readonly id: string;
  readonly turnId: string;
  readonly blockIndex: number;
  readonly text: string;
  readonly done: boolean;
}

export interface MessageItem {
  readonly kind: 'message';
  readonly id: string;
  readonly role: 'user' | 'assistant' | 'system';
  readonly content: string;
  readonly turnId: string | null;
  readonly createdAt: number;
  readonly seq: number;
  readonly pending: boolean;
}

export interface ToolItem {
  readonly kind: 'tool';
  readonly id: string;
  readonly toolCallId: string;
  readonly turnId: string;
  readonly name: string;
  readonly label: string;
  readonly inputSummary: string | null;
  readonly input: unknown;
  readonly risk: Risk | null;
  readonly status: ToolCallStatus;
  readonly isError: boolean;
  readonly resultSummary: string | null;
  readonly resultBytes: number | null;
  readonly durationMs: number | null;
  readonly errorHint: string | null;
  readonly autoAllowed: 'grant' | 'mode' | null;
  readonly gatedBy: ToolGate | null;
  readonly approvalId: string | null;
}

export interface ApprovalResolution {
  readonly decision: ApprovalDecision;
  readonly scope: ApprovalScope;
  readonly note: string | null;
  readonly source: ApprovalSource;
  readonly decidedAt: number;
}

export interface ApprovalItem {
  readonly kind: 'approval';
  readonly id: string;
  /** Mirrors `approval.turnId` so every ChatItem sorts into the same turn. */
  readonly turnId: string;
  readonly approval: ApprovalView;
  readonly resolution: ApprovalResolution | null;
  /** Set when the server refused the resolve (410 gone / expired). */
  readonly gone: boolean;
}

export type ChatItem = TextItem | MessageItem | ToolItem | ApprovalItem;

export interface TurnUsageState {
  readonly costUsd: number | null;
  readonly treeCostUsd: number | null;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheCreationTokens: number;
  readonly numTurns: number;
  readonly costAvailable: boolean;
}

export interface WorkspaceChange {
  readonly created: readonly string[];
  readonly modified: readonly string[];
  readonly deleted: readonly string[];
}

export interface TurnState {
  readonly id: string;
  readonly threadId: string;
  readonly status: 'running' | 'succeeded' | 'failed' | 'interrupted' | 'orphaned';
  readonly model: string | null;
  readonly startedAt: number | null;
  readonly sdkSessionId: string | null;
  readonly resumed: boolean;
  readonly usage: TurnUsageState | null;
  readonly resultSubtype: string | null;
  readonly stopReason: string | null;
  readonly durationMs: number | null;
  readonly toolCallCount: number;
  readonly interruptedReason: string | null;
  readonly error: { code: string; message: string; retryable: boolean } | null;
  readonly workspace: WorkspaceChange | null;
  readonly sawText: boolean;
}

export interface ThreadStreamState {
  readonly order: readonly string[];
  readonly byId: Readonly<Record<string, ChatItem>>;
  readonly turns: Readonly<Record<string, TurnState>>;
  readonly turnOrder: readonly string[];
  readonly activeTurnId: string | null;
  readonly hydrated: boolean;
}

export interface NoticeItem {
  readonly id: number;
  readonly level: 'info' | 'warn' | 'error';
  readonly code: string;
  readonly message: string;
  readonly at: number;
}

export interface SpaceCommitItem {
  readonly slug: string;
  readonly sha: string;
  readonly authorLabel: string;
  readonly message: string;
  readonly files: readonly string[];
  readonly at: number;
}

export interface EmployeeStreamState {
  readonly state: RunnerState;
  readonly pendingRecreate: boolean;
  readonly queuePosition: number | null;
  readonly detail: string;
}

export interface StreamState {
  readonly connection: ConnectionStatus;
  readonly userId: string | null;
  readonly gen: string | null;
  readonly serverTime: number | null;
  readonly credential: CredentialInfo | null;
  readonly employee: EmployeeStreamState | null;
  readonly maintenance: MaintenanceInfo;
  readonly pendingApprovals: readonly ApprovalView[];
  readonly notices: readonly NoticeItem[];
  readonly spaceCommits: readonly SpaceCommitItem[];
  readonly threads: Readonly<Record<string, ThreadStreamState>>;
  /** Bumped on every `resync`; the view refetches when it changes. */
  readonly resyncNonce: number;
  readonly resyncReason: string | null;
  readonly noticeSeq: number;
  readonly helloAt: number | null;
}

export const emptyThread: ThreadStreamState = {
  order: [],
  byId: {},
  turns: {},
  turnOrder: [],
  activeTurnId: null,
  hydrated: false,
};

export const initialStreamState: StreamState = {
  connection: 'idle',
  userId: null,
  gen: null,
  serverTime: null,
  credential: null,
  employee: null,
  maintenance: null,
  pendingApprovals: [],
  notices: [],
  spaceCommits: [],
  threads: {},
  resyncNonce: 0,
  resyncReason: null,
  noticeSeq: 0,
  helloAt: null,
};

/* ── ids ──────────────────────────────────────────────────────────────────── */

export const textItemId = (turnId: string, blockIndex: number): string =>
  `txt:${turnId}:${blockIndex}`;
export const toolItemId = (toolCallId: string): string => `tool:${toolCallId}`;
export const approvalItemId = (approvalId: string): string => `apr:${approvalId}`;
export const messageItemId = (messageId: string): string => `msg:${messageId}`;

const TERMINAL_TOOL_STATUS: readonly ToolCallStatus[] = ['ok', 'error', 'denied', 'interrupted'];

function isTerminal(status: ToolCallStatus): boolean {
  return TERMINAL_TOOL_STATUS.includes(status);
}

/* ── helpers ──────────────────────────────────────────────────────────────── */

function getThread(state: StreamState, threadId: string): ThreadStreamState {
  return state.threads[threadId] ?? emptyThread;
}

function withThread(
  state: StreamState,
  threadId: string,
  next: ThreadStreamState,
): StreamState {
  return { ...state, threads: { ...state.threads, [threadId]: next } };
}

function upsertItem(thread: ThreadStreamState, item: ChatItem): ThreadStreamState {
  const exists = thread.byId[item.id] !== undefined;
  return {
    ...thread,
    order: exists ? thread.order : [...thread.order, item.id],
    byId: { ...thread.byId, [item.id]: item },
  };
}

function blankTool(id: string, turnId: string): ToolItem {
  return {
    kind: 'tool',
    id: toolItemId(id),
    toolCallId: id,
    turnId,
    name: '',
    label: '',
    inputSummary: null,
    input: undefined,
    risk: null,
    status: 'running',
    isError: false,
    resultSummary: null,
    resultBytes: null,
    durationMs: null,
    errorHint: null,
    autoAllowed: null,
    gatedBy: null,
    approvalId: null,
  };
}

function existingTool(thread: ThreadStreamState, toolCallId: string, turnId: string): ToolItem {
  const found = thread.byId[toolItemId(toolCallId)];
  return found && found.kind === 'tool' ? found : blankTool(toolCallId, turnId);
}

function blankTurn(turnId: string, threadId: string): TurnState {
  return {
    id: turnId,
    threadId,
    status: 'running',
    model: null,
    startedAt: null,
    sdkSessionId: null,
    resumed: false,
    usage: null,
    resultSubtype: null,
    stopReason: null,
    durationMs: null,
    toolCallCount: 0,
    interruptedReason: null,
    error: null,
    workspace: null,
    sawText: false,
  };
}

function patchTurn(
  thread: ThreadStreamState,
  threadId: string,
  turnId: string,
  patch: Partial<TurnState>,
): ThreadStreamState {
  const current = thread.turns[turnId] ?? blankTurn(turnId, threadId);
  return {
    ...thread,
    turns: { ...thread.turns, [turnId]: { ...current, ...patch } },
    turnOrder: thread.turnOrder.includes(turnId) ? thread.turnOrder : [...thread.turnOrder, turnId],
  };
}

/* ── event application ────────────────────────────────────────────────────── */

/** Applies one CONTRACTS §5 event. Pure; safe to replay. */
export function applyEvent(state: StreamState, event: SseEvent): StreamState {
  switch (event.event) {
    case 'hello': {
      const d = event.data;
      // A `hello` naming a different user means this tab was re-authenticated
      // as somebody else. Whatever the previous user accumulated — transcripts,
      // approvals, notices — is theirs, and must not be carried into the new
      // session even for the moment it takes REST to answer. The connection
      // status is the one fact that survives: it describes the socket, not the
      // session. (`reset` on /login is the primary guard; this is the one that
      // holds when the tab never passed through /login.)
      const base =
        state.userId !== null && state.userId !== d.userId
          ? { ...initialStreamState, connection: state.connection }
          : state;
      return {
        ...base,
        userId: d.userId,
        gen: d.gen,
        serverTime: d.serverTime,
        credential: d.credential,
        employee: {
          state: d.employee.state,
          pendingRecreate: d.employee.pendingRecreate,
          queuePosition: null,
          detail: '',
        },
        maintenance: d.maintenance,
        pendingApprovals: d.pendingApprovals,
        helloAt: Date.now(),
      };
    }

    case 'turn.started': {
      const d = event.data;
      const thread = patchTurn(getThread(state, d.threadId), d.threadId, d.turnId, {
        status: 'running',
        model: d.model,
        startedAt: Date.now(),
      });
      return withThread(state, d.threadId, { ...thread, activeTurnId: d.turnId });
    }

    case 'session.bound': {
      const d = event.data;
      const thread = patchTurn(getThread(state, d.threadId), d.threadId, d.turnId, {
        sdkSessionId: d.sdkSessionId,
        resumed: d.resumed,
      });
      return withThread(state, d.threadId, thread);
    }

    case 'text.delta': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      const id = textItemId(d.turnId, d.blockIndex);
      const current = thread.byId[id];
      const previous = current && current.kind === 'text' ? current.text : '';
      const next = upsertItem(thread, {
        kind: 'text',
        id,
        turnId: d.turnId,
        blockIndex: d.blockIndex,
        text: previous + d.text,
        done: false,
      });
      return withThread(
        state,
        d.threadId,
        patchTurn(next, d.threadId, d.turnId, { sawText: true }),
      );
    }

    case 'text.done': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      const id = textItemId(d.turnId, d.blockIndex);
      const current = thread.byId[id];
      const text = current && current.kind === 'text' ? current.text : '';
      return withThread(
        state,
        d.threadId,
        upsertItem(thread, {
          kind: 'text',
          id,
          turnId: d.turnId,
          blockIndex: d.blockIndex,
          text,
          done: true,
        }),
      );
    }

    case 'tool.start': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      const prior = existingTool(thread, d.toolCallId, d.turnId);
      // A late `tool.start` must not resurrect a settled card.
      return withThread(
        state,
        d.threadId,
        upsertItem(thread, { ...prior, name: d.name, label: d.label, turnId: d.turnId }),
      );
    }

    case 'tool.input': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      const prior = existingTool(thread, d.toolCallId, d.turnId);
      return withThread(
        state,
        d.threadId,
        upsertItem(thread, {
          ...prior,
          inputSummary: d.inputSummary,
          input: d.input,
          risk: d.risk,
        }),
      );
    }

    case 'tool.end': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      const prior = existingTool(thread, d.toolCallId, d.turnId);
      const merged: ToolItem = {
        ...prior,
        status: d.status,
        isError: d.isError,
        resultSummary: d.resultSummary,
        resultBytes: d.resultBytes,
        durationMs: d.durationMs,
        errorHint: d.errorHint ?? null,
      };
      const next = upsertItem(thread, merged);
      const turn = next.turns[d.turnId];
      return withThread(
        state,
        d.threadId,
        patchTurn(next, d.threadId, d.turnId, {
          toolCallCount: (turn?.toolCallCount ?? 0) + (isTerminal(prior.status) ? 0 : 1),
        }),
      );
    }

    case 'tool.auto_allowed': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      const prior = existingTool(thread, d.toolCallId, d.turnId);
      return withThread(
        state,
        d.threadId,
        upsertItem(thread, {
          ...prior,
          autoAllowed: d.reason,
          gatedBy: d.reason === 'grant' ? 'always_allow' : 'mode',
        }),
      );
    }

    case 'approval.requested': {
      const approval = event.data;
      const thread = getThread(state, approval.threadId);
      let next = upsertItem(thread, {
        kind: 'approval',
        id: approvalItemId(approval.id),
        turnId: approval.turnId,
        approval,
        resolution: null,
        gone: false,
      });
      if (approval.toolCallId) {
        const prior = existingTool(next, approval.toolCallId, approval.turnId);
        next = upsertItem(next, { ...prior, approvalId: approval.id, gatedBy: 'approval' });
      }
      const already = state.pendingApprovals.some((a) => a.id === approval.id);
      return {
        ...withThread(state, approval.threadId, next),
        pendingApprovals: already ? state.pendingApprovals : [...state.pendingApprovals, approval],
      };
    }

    case 'approval.resolved': {
      const d = event.data;
      const resolution: ApprovalResolution = {
        decision: d.decision,
        scope: d.scope,
        note: d.note,
        source: d.source,
        decidedAt: d.decidedAt,
      };
      let threads = state.threads;
      for (const [threadId, thread] of Object.entries(state.threads)) {
        const item = thread.byId[approvalItemId(d.approvalId)];
        if (item && item.kind === 'approval') {
          threads = {
            ...threads,
            [threadId]: upsertItem(thread, { ...item, resolution }),
          };
        }
      }
      return {
        ...state,
        threads,
        pendingApprovals: state.pendingApprovals.filter((a) => a.id !== d.approvalId),
      };
    }

    case 'turn.usage': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      return withThread(
        state,
        d.threadId,
        patchTurn(thread, d.threadId, d.turnId, {
          usage: {
            costUsd: d.costUsd,
            treeCostUsd: d.treeCostUsd,
            inputTokens: d.inputTokens,
            outputTokens: d.outputTokens,
            cacheReadTokens: d.cacheReadTokens,
            cacheCreationTokens: d.cacheCreationTokens,
            numTurns: d.numTurns,
            costAvailable: d.costAvailable,
          },
        }),
      );
    }

    case 'turn.finished': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      const next = patchTurn(thread, d.threadId, d.turnId, {
        status: d.status,
        resultSubtype: d.resultSubtype,
        stopReason: d.stopReason,
        durationMs: d.durationMs,
        toolCallCount: d.toolCallCount,
      });
      return withThread(state, d.threadId, {
        ...next,
        activeTurnId: next.activeTurnId === d.turnId ? null : next.activeTurnId,
      });
    }

    case 'turn.interrupted': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      const next = patchTurn(thread, d.threadId, d.turnId, {
        status: 'interrupted',
        interruptedReason: d.reason,
      });
      return withThread(state, d.threadId, {
        ...next,
        activeTurnId: next.activeTurnId === d.turnId ? null : next.activeTurnId,
      });
    }

    case 'turn.error': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      const next = patchTurn(thread, d.threadId, d.turnId, {
        error: { code: d.code, message: d.message, retryable: d.retryable },
      });
      return withThread(state, d.threadId, next);
    }

    case 'workspace.changed': {
      const d = event.data;
      const thread = getThread(state, d.threadId);
      return withThread(
        state,
        d.threadId,
        patchTurn(thread, d.threadId, d.turnId, {
          workspace: { created: d.created, modified: d.modified, deleted: d.deleted },
        }),
      );
    }

    case 'space.committed': {
      const d = event.data;
      const commit: SpaceCommitItem = {
        slug: d.slug,
        sha: d.sha,
        authorLabel: d.authorLabel,
        message: d.message,
        files: d.files,
        at: Date.now(),
      };
      return { ...state, spaceCommits: [commit, ...state.spaceCommits].slice(0, 20) };
    }

    case 'employee.status': {
      const d = event.data;
      return {
        ...state,
        employee: {
          state: d.state,
          pendingRecreate: d.pendingRecreate,
          queuePosition: d.queuePosition ?? null,
          detail: d.detail,
        },
      };
    }

    case 'credential.changed':
      return { ...state, credential: event.data };

    case 'notice': {
      const d = event.data;
      const id = state.noticeSeq + 1;
      return {
        ...state,
        noticeSeq: id,
        notices: [
          ...state.notices,
          { id, level: d.level, code: d.code, message: d.message, at: Date.now() },
        ].slice(-5),
      };
    }

    case 'resync':
      return {
        ...state,
        resyncNonce: state.resyncNonce + 1,
        resyncReason: event.data.reason,
      };

    default: {
      // Unknown `event:` names are tolerated: the gateway may add one before a
      // client is redeployed, and dropping the stream over it would be worse.
      return state;
    }
  }
}

/* ── non-SSE actions ──────────────────────────────────────────────────────── */

export interface LocalUserMessage {
  readonly threadId: string;
  readonly id: string;
  readonly text: string;
  readonly seq: number;
  readonly turnId: string | null;
  readonly createdAt: number;
}

export type StreamAction =
  | { readonly type: 'events'; readonly events: readonly SseEvent[] }
  | { readonly type: 'connection'; readonly status: ConnectionStatus }
  | { readonly type: 'hydrate'; readonly threadId: string; readonly detail: ThreadDetailResponse }
  | {
      readonly type: 'prepend';
      readonly threadId: string;
      readonly messages: readonly MessageView[];
      readonly toolCalls: readonly ToolCallView[];
    }
  | { readonly type: 'localMessage'; readonly message: LocalUserMessage }
  | { readonly type: 'localMessageFailed'; readonly threadId: string; readonly id: string }
  | { readonly type: 'approvalGone'; readonly approvalId: string }
  | { readonly type: 'dismissNotice'; readonly id: number }
  | { readonly type: 'reset' };

function messageToItem(message: MessageView, pending = false): MessageItem {
  return {
    kind: 'message',
    id: messageItemId(message.id),
    role: message.role,
    content: message.content,
    turnId: message.turnId,
    createdAt: message.createdAt,
    seq: message.seq,
    pending,
  };
}

function toolViewToItem(view: ToolCallView): ToolItem {
  return {
    kind: 'tool',
    id: toolItemId(view.id),
    toolCallId: view.id,
    turnId: view.turnId,
    name: view.name,
    label: view.label,
    inputSummary: view.inputSummary,
    input: view.input,
    risk: null,
    status: view.status,
    isError: view.isError,
    resultSummary: view.resultSummary,
    resultBytes: null,
    durationMs: view.durationMs,
    errorHint: null,
    autoAllowed: view.gatedBy === 'always_allow' ? 'grant' : view.gatedBy === 'mode' ? 'mode' : null,
    gatedBy: view.gatedBy,
    approvalId: view.approvalId,
  };
}

function buildHistorical(
  messages: readonly MessageView[],
  toolCalls: readonly ToolCallView[],
  approvals: readonly ApprovalView[],
): { order: string[]; byId: Record<string, ChatItem> } {
  const rows: { sortKey: number; item: ChatItem }[] = [];
  for (const message of messages) rows.push({ sortKey: message.seq, item: messageToItem(message) });
  for (const call of toolCalls) {
    // Tool seq shares the message sequence space; the +0.5 keeps a tool call
    // after the message it belongs to when both carry the same seq.
    rows.push({ sortKey: call.seq + 0.5, item: toolViewToItem(call) });
  }
  rows.sort((a, b) => a.sortKey - b.sortKey);
  for (const approval of approvals) {
    rows.push({
      sortKey: Number.MAX_SAFE_INTEGER,
      item: {
        kind: 'approval',
        id: approvalItemId(approval.id),
        turnId: approval.turnId,
        approval,
        resolution: null,
        gone: false,
      },
    });
  }
  const order: string[] = [];
  const byId: Record<string, ChatItem> = {};
  for (const row of rows) {
    if (byId[row.item.id] === undefined) order.push(row.item.id);
    byId[row.item.id] = row.item;
  }
  return { order, byId };
}

export function streamReducer(state: StreamState, action: StreamAction): StreamState {
  switch (action.type) {
    case 'events': {
      let next = state;
      for (const event of action.events) next = applyEvent(next, event);
      return next;
    }

    case 'connection':
      return state.connection === action.status ? state : { ...state, connection: action.status };

    case 'hydrate': {
      const { threadId, detail } = action;
      const previous = getThread(state, threadId);
      const historical = buildHistorical(detail.messages, detail.toolCalls, detail.pendingApprovals);

      // Turns whose assistant words Tier A has already mirrored into `messages`.
      // A live text block and its persisted row are the same sentence under two
      // ids (`txt:<turn>:<block>` vs `msg:<id>`), so id equality can NEVER
      // reconcile them and keeping both renders the reply twice on every
      // re-hydration of a settled turn (rename, list → back, `resync`).
      const mirroredTurns = new Set<string>();
      for (const message of detail.messages) {
        if (message.role === 'assistant' && message.turnId !== null) {
          mirroredTurns.add(message.turnId);
        }
      }
      const activeTurnId = detail.activeTurn?.id ?? null;

      // Overlay: keep live items (streaming text, tool cards, approvals) the
      // REST payload does not carry, in their existing relative order.
      const order = [...historical.order];
      const byId: Record<string, ChatItem> = { ...historical.byId };
      for (const id of previous.order) {
        const item = previous.byId[id];
        if (item === undefined) continue;
        if (byId[id] !== undefined) {
          // Server-known items win, except a live approval resolution the REST
          // snapshot cannot express.
          const merged = byId[id];
          if (item.kind === 'approval' && merged.kind === 'approval' && item.resolution) {
            byId[id] = { ...merged, resolution: item.resolution, gone: item.gone };
          }
          continue;
        }
        // A settled turn the payload covers: REST wins, exactly as it already
        // does for tool calls. Both narrowings earn their place — text of the
        // turn still running is the live tail the mirror cannot see yet, and
        // text of a turn the mirror has not caught up with is the only copy of
        // those words this client holds.
        const settledText =
          item.kind === 'text' && item.turnId !== activeTurnId && mirroredTurns.has(item.turnId);
        if (settledText) continue;
        if (item.kind === 'message' && item.pending) {
          // A local echo the server has since persisted under its own id.
          const persisted = detail.messages.some(
            (m) => m.role === 'user' && m.content === item.content,
          );
          if (persisted) continue;
        }
        order.push(id);
        byId[id] = item;
      }

      const turns = { ...previous.turns };
      const turnOrder = [...previous.turnOrder];
      if (detail.activeTurn) {
        const id = detail.activeTurn.id;
        if (turns[id] === undefined) {
          turns[id] = { ...blankTurn(id, threadId), startedAt: detail.activeTurn.startedAt };
          turnOrder.push(id);
        }
      }

      return withThread(state, threadId, {
        order,
        byId,
        turns,
        turnOrder,
        activeTurnId: detail.activeTurn ? detail.activeTurn.id : previous.activeTurnId,
        hydrated: true,
      });
    }

    case 'prepend': {
      const thread = getThread(state, action.threadId);
      const older = buildHistorical(action.messages, action.toolCalls, []);
      const order = [...older.order.filter((id) => thread.byId[id] === undefined), ...thread.order];
      return withThread(state, action.threadId, {
        ...thread,
        order,
        byId: { ...older.byId, ...thread.byId },
      });
    }

    case 'localMessage': {
      const m = action.message;
      const thread = getThread(state, m.threadId);
      return withThread(
        state,
        m.threadId,
        upsertItem(thread, {
          kind: 'message',
          id: messageItemId(m.id),
          role: 'user',
          content: m.text,
          turnId: m.turnId,
          createdAt: m.createdAt,
          seq: m.seq,
          pending: true,
        }),
      );
    }

    case 'localMessageFailed': {
      const thread = getThread(state, action.threadId);
      const id = messageItemId(action.id);
      if (thread.byId[id] === undefined) return state;
      const { [id]: _removed, ...rest } = thread.byId;
      return withThread(state, action.threadId, {
        ...thread,
        order: thread.order.filter((entry) => entry !== id),
        byId: rest,
      });
    }

    case 'approvalGone': {
      let threads = state.threads;
      for (const [threadId, thread] of Object.entries(state.threads)) {
        const item = thread.byId[approvalItemId(action.approvalId)];
        if (item && item.kind === 'approval') {
          threads = { ...threads, [threadId]: upsertItem(thread, { ...item, gone: true }) };
        }
      }
      return {
        ...state,
        threads,
        pendingApprovals: state.pendingApprovals.filter((a) => a.id !== action.approvalId),
      };
    }

    case 'dismissNotice':
      return { ...state, notices: state.notices.filter((n) => n.id !== action.id) };

    case 'reset':
      return initialStreamState;

    default:
      return state;
  }
}

/* ── selectors ────────────────────────────────────────────────────────────── */

export function selectThread(state: StreamState, threadId: string | undefined): ThreadStreamState {
  if (!threadId) return emptyThread;
  return state.threads[threadId] ?? emptyThread;
}

export function selectItems(thread: ThreadStreamState): ChatItem[] {
  const items: ChatItem[] = [];
  for (const id of thread.order) {
    const item = thread.byId[id];
    if (item !== undefined) items.push(item);
  }
  return items;
}

export function selectTurn(thread: ThreadStreamState, turnId: string): TurnState | null {
  return thread.turns[turnId] ?? null;
}

export function selectPendingApprovalCount(state: StreamState): number {
  return state.pendingApprovals.length;
}
